import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

const Candidate = ({ c, onLink, busy }) => (
  <li className="flex flex-wrap items-center justify-between gap-3 py-2" data-testid={`pco-candidate-${c.id}`}>
    <div className="text-sm">
      <p className="text-navy font-medium">{c.name} <span className="text-muted-ink font-normal">· #{c.id}</span></p>
      <p className="text-muted-ink">{c.emails.filter(Boolean).join(", ") || "No email"}</p>
    </div>
    <button disabled={busy} onClick={onLink} className="chip-btn" data-testid={`pco-link-${c.id}`}>Same person, link</button>
  </li>
);

export const PcoReview = ({ onChange }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const load = () => api.get("/integrations/pco/review").then((r) => { setItems(r.data); setErr(false); }).catch(() => setErr(true));
  useEffect(() => { load(); }, []);

  const resolve = async (vid, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/integrations/pco/review/${vid}`, body);
      data.pco_status === "error" ? toast.error(data.pco_error) : toast.success(body.action === "create" ? "New person added to Planning Center" : "Linked to existing person");
      load();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resolve");
    } finally {
      setBusy(false);
    }
  };

  if (err) return <p className="text-sm text-red-700 mt-6" data-testid="pco-review-error">Couldn't load the duplicate review list. Refresh to try again.</p>;
  if (!items.length) return <p className="text-sm text-muted-ink mt-6" data-testid="pco-review-empty">No possible duplicates to review.</p>;
  return (
    <div className="mt-6" data-testid="pco-review">
      <p className="text-xs uppercase tracking-[0.12em] text-muted-ink flex items-center gap-2"><AlertTriangle size={14} className="text-amber-600" /> Needs your review ({items.length})</p>
      {items.map((v) => (
        <div key={v.volunteer_id} className="border border-[var(--line)] rounded-xl p-4 mt-3" data-testid={`pco-review-${v.volunteer_id}`}>
          <p className="text-navy font-medium"><Link to={`/volunteers/${v.volunteer_id}`} className="underline decoration-dotted hover:text-teal" data-testid={`pco-review-link-${v.volunteer_id}`}>{v.name}</Link> <span className="text-muted-ink font-normal text-sm">· {v.email}{v.phone ? ` · ${v.phone}` : ""}</span></p>
          <p className="text-sm text-amber-700 mt-1">{v.pco_review?.reason}</p>
          <ul className="divide-y divide-[var(--line)] mt-2">
            {(v.pco_review?.candidates || []).map((c) => <Candidate key={c.id} c={c} busy={busy} onLink={() => resolve(v.volunteer_id, { action: "link", person_id: c.id })} />)}
          </ul>
          <button disabled={busy} onClick={() => resolve(v.volunteer_id, { action: "create" })} className="text-sm text-navy underline underline-offset-4 mt-3" data-testid={`pco-create-new-${v.volunteer_id}`}>Different person, create new profile</button>
        </div>
      ))}
    </div>
  );
};
